import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiCheck, FiCreditCard, FiZap } = FiIcons;

const plans = [
  {
    id: 'monthly',
    name: "月額利用料",
    nameEn: "Monthly Plan",
    amount: 390,
    currency: "jpy",
    interval: "month",
    intervalText: "月",
    intervalTextEn: "month",
    paymentLink: "https://buy.stripe.com/test_4gM5kDf54cAVgIycC55gc00", // Placeholder or specific link
    features: [
      { text: "背景自動透過（無制限）", included: true },
      { text: "一括エクスポート機能", included: true },
      { text: "エッジ補正（色被り除去）", included: true },
      { text: "手動消しゴムツール", included: true },
      { text: "スポイトでカラー抽出", included: true },
      { text: "高品質 PNG 保存", included: true },
    ]
  },
  {
    id: 'yearly',
    name: "年間利用料",
    nameEn: "Annual Plan",
    amount: 890,
    currency: "jpy",
    interval: "year",
    intervalText: "年",
    intervalTextEn: "year",
    paymentLink: "https://buy.stripe.com/test_4gM5kDf54cAVgIycC55gc00",
    features: [
      { text: "背景自動透過（無制限）", included: true },
      { text: "一括エクスポート機能", included: true },
      { text: "エッジ補正（色被り除去）", included: true },
      { text: "手動消しゴムツール", included: true },
      { text: "スポイトでカラー抽出", included: true },
      { text: "高品質 PNG 保存", included: true },
    ]
  }
];

const PricingModal = ({ isOpen, onClose, lang }) => {
  const [activePlanId, setActivePlanId] = useState('yearly');
  const activePlan = plans.find(p => p.id === activePlanId) || plans[1];

  const t = lang === 'jp'
    ? { title: "プラン", subtitle: "最適なプランをお選びください", per: "/", getStarted: "今すぐ始める", popular: "おすすめ", close: "閉じる", monthly: "月払い", yearly: "年払い (お得!)" }
    : { title: "Pricing", subtitle: "Choose the plan that fits your needs", per: "/", getStarted: "Get Started", popular: "Popular", close: "Close", monthly: "Monthly", yearly: "Yearly (Best Value!)" };

  const handlePaymentClick = (paymentLink) => {
    window.open(paymentLink, '_blank');
  };

  // Robust close handler to ensure event propagation doesn't interfere
  const handleClose = (e) => {
    if (e) e.stopPropagation();
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[120] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.9, y: 30 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 30 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="bg-white rounded-[2rem] w-full max-w-[360px] shadow-2xl border border-slate-100 overflow-hidden mx-auto flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative bg-gradient-to-br from-indigo-600 via-indigo-500 to-purple-600 p-6 pb-8 text-white overflow-hidden shrink-0">
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-2xl" />
              <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-purple-400/20 rounded-full blur-2xl" />
              
              {/* Close Button - Using handleClose explicitly */}
              <button
                type="button"
                onClick={handleClose}
                className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center hover:bg-white/30 transition-all active:scale-90 z-[130]"
                aria-label={t.close}
              >
                <SafeIcon icon={FiX} size={18} className="text-white" />
              </button>

              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-1.5 bg-white/20 rounded-lg">
                    <SafeIcon icon={FiZap} size={16} />
                  </div>
                  <span className="text-[10px] font-black uppercase tracking-widest bg-white/20 px-2.5 py-0.5 rounded-full">
                    {t.popular}
                  </span>
                </div>
                <h2 className="text-xl font-black tracking-tight">{t.title}</h2>
                <p className="text-xs text-white/70 font-medium mt-1">{t.subtitle}</p>
              </div>
            </div>

            {/* Plan Selector */}
            <div className="px-6 pt-6 shrink-0">
              <div className="bg-slate-100 p-1 rounded-2xl flex items-center shadow-inner border border-slate-200/50">
                <button
                  onClick={() => setActivePlanId('monthly')}
                  className={`flex-1 py-2.5 text-[11px] font-black rounded-[14px] transition-all active:scale-95 ${activePlanId === 'monthly' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  {t.monthly}
                </button>
                <button
                  onClick={() => setActivePlanId('yearly')}
                  className={`flex-1 py-2.5 text-[11px] font-black rounded-[14px] transition-all active:scale-95 ${activePlanId === 'yearly' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  {t.yearly}
                </button>
              </div>
            </div>

            {/* Scrollable Content Container */}
            <div className="overflow-y-auto p-6 flex-1 custom-scrollbar">
              <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100 mb-5 text-center relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-16 h-16 bg-indigo-500/5 rounded-full -mr-8 -mt-8 group-hover:scale-150 transition-transform duration-700" />
                <div className="flex items-end justify-center gap-1 mb-1">
                  <span className="text-4xl font-black text-slate-900 tracking-tight transition-transform group-hover:scale-105 duration-300">
                    ¥{activePlan.amount}
                  </span>
                  <span className="text-sm font-bold text-slate-400 mb-1.5">
                    {t.per}{lang === 'jp' ? activePlan.intervalText : activePlan.intervalTextEn}
                  </span>
                </div>
                <p className="text-xs font-bold text-slate-500">{lang === 'jp' ? activePlan.name : activePlan.nameEn}</p>
              </div>

              {/* Features */}
              <div className="space-y-3 mb-8">
                {activePlan.features.map((feature, idx) => (
                  <motion.div
                    key={`${activePlanId}-${idx}`}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 shadow-sm">
                      <SafeIcon icon={FiCheck} size={12} className="text-emerald-600" />
                    </div>
                    <span className="text-sm font-semibold text-slate-700">{feature.text}</span>
                  </motion.div>
                ))}
              </div>

              {/* CTA Button */}
              <button
                onClick={() => handlePaymentClick(activePlan.paymentLink)}
                className="w-full h-14 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white text-sm font-black rounded-2xl flex items-center justify-center gap-2.5 transition-all shadow-lg shadow-indigo-500/30 active:scale-[0.98] hover:shadow-indigo-500/50"
              >
                <SafeIcon icon={FiCreditCard} size={18} />
                {t.getStarted}
              </button>

              <p className="text-center text-[10px] text-slate-400 font-medium mt-4 pb-2">
                {lang === 'jp' ? 'Stripe による安全な決済' : 'Secure payment via Stripe'}
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PricingModal;